import pica, { type Pica } from 'pica';
import type { Viewer } from '../components/AppViewer/Viewer';

export type ImageType = 'image/jpeg' | 'image/png' | 'image/webp';

const picaOnce = (() => {
  let picaInstance: Pica | null = null;
  return () => {
    if (picaInstance === null) {
      picaInstance = pica();
    }
    return picaInstance;
  };
})();

export function captureImage(
  viewer: Viewer,
  type?: ImageType,
  quality?: number,
): Promise<Blob | null> {
  // For Chrome (which uses the Blink engine), the default quality parameters are:
  // - JPEG (image/jpeg): 0.92 (92%)
  // - WebP (image/webp): 0.80 (80%)
  // Raise WebP defaults as image is being compressed significantly out-of-the-box
  if (quality === undefined && type === 'image/webp') {
    quality = 0.92;
  }
  const canvas = viewer.canvasRef.current!;
  return new Promise(resolve => {
    canvas.toBlob(resolve, type, quality);
  });
}

export async function captureImagePreview(
  viewer: Viewer,
  width: number,
  height: number,
  type?: ImageType,
  quality?: number,
): Promise<Blob> {
  type ??= 'image/webp';
  if (quality === undefined && type === 'image/webp') {
    quality = 0.92;
  }
  const canvas = viewer.canvasRef.current!;
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = width;
  resizedCanvas.height = height;
  const picaInstance = picaOnce();
  await picaInstance.resize(canvas, resizedCanvas);
  return picaInstance.toBlob(resizedCanvas, type, quality);
}

function toExtension(type?: ImageType) {
  switch (type) {
    case 'image/webp':
      return 'webp';
    case 'image/jpeg':
      return 'jpeg';
    default:
      return 'png';
  }
}

export async function downloadImage(viewer: Viewer, type?: ImageType, quality?: number) {
  const blob = await captureImage(viewer, type, quality);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob!);
  link.download = `screenshot.${toExtension(type)}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  link.remove();
}
